import { useState, useEffect, useContext, useReducer } from "react";
import styled from "styled-components";
import isEqual from "lodash.isequal";
import { useTranslation } from "react-i18next";

import { MediaQueries, Spacing } from "@paclife/plds.tokens";
import { Button } from "@paclife/plds.button";

import API from "api/api";
import PasswordIcon from "assets/svg/password.svg";

import { AppContext } from "context/AppContext";
import Card from "common/Card/Card";
import TextField from "common/TextField/TextField";
import TYPE from "constants/TextFieldType";
import Message from "common/Message/Message";

import PwdValidation from "./PwdValidation";
import Typography from "common/Typography/Typography";
import CircularProgress from "common/CircularProgress/CircularProgress";

const CustomGrid = styled.div`
    display: grid;
    grid-template-columns: minmax(150px, 1fr);
    @media (min-width: ${MediaQueries.md}) {
        // grid-template-columns: repeat(2, minmax(150px, 1fr));
        grid-template-columns: 2fr 2.5fr;
    }
    @media (min-width: ${MediaQueries.lg}) {
        // grid-template-columns: repeat(3, minmax(150px, 1fr));
        grid-template-columns: 2fr 2.5fr 1.5fr;
    }
    gap: 24px;
    .text-field-bottom-margin {
        margin-bottom: ${Spacing[16]};
    }
`;

const Image = styled.img`
    width: 120px;
    grid-column: 3 / 4;
    grid-row: 1 / 3;
    justify-self: center;
    align-self: center;
    @media (max-width: ${MediaQueries.lg}) {
        display: none;
    }
`;

const ButtonGroup = styled.div`
    @media (min-width: ${MediaQueries.md}) {
        grid-column: 1 / 3;
    }
    .margin-right {
        margin-right: ${Spacing[16]};
    }
`;

const initState = {
    oldPassword: "",
    newPassword: "",
    verifyNewPassword: ""
};

const reducer = (state, action) => {
    switch (action.type) {
        case "UPDATE":
            return { ...state, ...action.payload };
        case "RESET":
            return initState;
        default:
            throw new Error(`Unhandled action type ${action.type}`);
    }
};

const UpdatePassword = () => {
    const { t } = useTranslation("password");

    const {
        personal: { username, firstName, lastName },
        userSession
    } = useContext(AppContext);

    const userInfo = {
        username,
        firstName,
        lastName
    };
    const { ssoUser, impersonation } = userSession.data.user;
    
    const [loading, setLoading] = useState(false);
    const [passwords, dispatch] = useReducer(reducer, initState);
    const [disableCancel, setDisableCancel] = useState(true);
    const [disableSubmit, setDisableSubmit] = useState(true);
    const [errorMsg, setErrorMsg] = useState("");
    const [successMsg, setSuccessMsg] = useState("");

    useEffect(() => {
        const timerId = setTimeout(() => {
            setErrorMsg("");
            setSuccessMsg("");
        }, 6000);
        return () => clearTimeout(timerId);
    }, [errorMsg, successMsg]);

    const updateCancelBtnState = () => {
        if (isEqual(passwords, initState)) {
            setDisableCancel(true);
        } else {
            setDisableCancel(false);
        }
    };

    const updateSubmitBtnState = () => {
        const pwRegex = /^(?=.*\d)(?!.*\s)(?=.*[a-zA-Z])(?=.*[!@#$%^&*]).{8,}$/gm;
        const namesRegex = new RegExp(
            `(\b|)(?:${username}|${firstName}|${lastName})(\b|)`,
            "ig"
        );

        const hasCurrentPw = passwords.oldPassword !== "";
        const validPwFormat = pwRegex.test(passwords.newPassword);
        const noNames = !namesRegex.test(passwords.newPassword);
        const match = passwords.newPassword === passwords.verifyNewPassword;
        const newDiffFromOld =
            passwords.oldPassword !== "" &&
            passwords.newPassword !== passwords.oldPassword;

        if (
            hasCurrentPw &&
            validPwFormat &&
            match &&
            noNames &&
            newDiffFromOld
        ) {
            setDisableSubmit(false);
        } else {
            setDisableSubmit(true);
        }
    };

    useEffect(() => {
        updateSubmitBtnState();
        updateCancelBtnState();
    }, [passwords]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        dispatch({ type: "UPDATE", payload: { [name]: value } }); 
    };

    const handleCancel = () => {
        dispatch({ type: "RESET" });
    };

    const handleSubmit = async () => {
        setLoading(true);
        const { succeed, message } = await API.updatePassword(passwords);
        setLoading(false);
        if (succeed) {
            setErrorMsg("");
            setSuccessMsg(message);
        } else {
            setErrorMsg(message);
            setSuccessMsg("");
        }
        dispatch({ type: "RESET" });
    };

    return (
        <Card title={t("title")}>
            {errorMsg && <Message message={errorMsg} type="error" />}
            {successMsg && <Message message={successMsg} type="success" />}
            <CustomGrid>
                <div>
                    <TextField
                        className="text-field-bottom-margin"
                        type={TYPE.password}
                        id="old-pw"
                        label="Current Password"
                        name="oldPassword"
                        value={passwords.oldPassword}
                        onChange={handleChange}
                    />
                    <TextField
                        className="text-field-bottom-margin"
                        type={TYPE.password}
                        id="new-pw"
                        label="New Password"
                        name="newPassword"
                        value={passwords.newPassword}
                        onChange={handleChange}
                        error={
                            passwords.oldPassword !== "" &&
                            passwords.newPassword === passwords.oldPassword
                        }
                        errorMessage={t("error.newSameAsOld")}
                    />
                    <TextField
                        className="text-field-bottom-margin"
                        type={TYPE.password}
                        id="verify-new-pw"
                        label="Verify New Password"
                        name="verifyNewPassword"
                        value={passwords.verifyNewPassword}
                        onChange={handleChange}
                        error={
                            passwords.verifyNewPassword !== "" &&
                            passwords.newPassword !==
                                passwords.verifyNewPassword
                        }
                        errorMessage={t("error.notMatch")}
                    />
                    {loading && (
                        <div>
                            <CircularProgress size={12} />{" "}
                            <Typography variant="caption">
                                Updating password...
                            </Typography>
                        </div>
                    )}
                </div>
                <div>
                    <PwdValidation
                        value={passwords.newPassword}
                        userInfo={userInfo}
                    />
                </div>
                <Image src={PasswordIcon} alt="password image" />
                <ButtonGroup>
                    <Button
                        variant="secondaryNeutral"
                        onClick={handleCancel}
                        className="margin-right"
                        disabled={disableCancel}
                        data-track="analytics"
                        data-bname="cancel-password-update"
                    >
                        {t("button.cancel")}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={disableSubmit || ssoUser || impersonation}
                        data-track="analytics"
                        data-bname="update-password"
                    >
                        {t("button.submit")}
                    </Button>
                </ButtonGroup>
            </CustomGrid>
        </Card>
    );
};

export default UpdatePassword;
